export type SurveyConfig = {
  provider_key: string;
  provider_host: string;
};

export type SurveyType = 'popover' | 'widget' | 'api' | 'external_survey';

export type SurveySchedule = 'once' | 'recurring' | 'always';

export type SurveyAppearancePosition = 'left' | 'right' | 'center';

export interface SurveyAppearance {
  position?: SurveyAppearancePosition;
  displayThankYouMessage?: boolean;
  thankYouMessageHeader?: string;
  thankYouMessageDescription?: string;
  thankYouMessageCloseButtonText?: string;
  placeholder?: string;
  shuffleQuestions?: boolean;
  whiteLabel?: boolean;
  zIndex?: string;
  // Admin-side hex defaults — intentionally ignored by the custom renderer.
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: string;
  boxPadding?: string;
  boxShadow?: string;
  disabledButtonOpacity?: string;
  fontFamily?: string;
  inputBackground?: string;
  maxWidth?: string;
  ratingButtonActiveColor?: string;
  ratingButtonColor?: string;
  submitButtonColor?: string;
  submitButtonTextColor?: string;
  tabPosition?: string;
  textSubtleColor?: string;
}

export type SurveyResponseBucket = 'negative' | 'neutral' | 'positive';

export type SurveyBranching =
  | {
      type: 'response_based';
      responseValues: Record<string, number | 'end'>;
    }
  | { type: 'specific_question'; index: number }
  | { type: 'end' };

export interface SurveyBaseQuestion {
  id: string;
  question: string;
  description?: string;
  descriptionContentType?: 'text' | 'html';
  buttonText?: string;
  optional?: boolean;
  skipSubmitButton?: boolean;
  branching?: SurveyBranching;
}

export type SurveyRatingScale = 3 | 5 | 7 | 10;

export interface SurveyRatingQuestion extends SurveyBaseQuestion {
  type: 'rating';
  display: 'emoji' | 'number';
  scale: SurveyRatingScale;
  lowerBoundLabel?: string;
  upperBoundLabel?: string;
  isNpsQuestion?: boolean;
}

export interface SurveyOpenQuestion extends SurveyBaseQuestion {
  type: 'open';
}

export interface SurveySingleChoiceQuestion extends SurveyBaseQuestion {
  type: 'single_choice';
  choices: string[];
  hasOpenChoice?: boolean;
  shuffleOptions?: boolean;
}

export interface SurveyMultipleChoiceQuestion extends SurveyBaseQuestion {
  type: 'multiple_choice';
  choices: string[];
  hasOpenChoice?: boolean;
  shuffleOptions?: boolean;
}

export interface SurveyLinkQuestion extends SurveyBaseQuestion {
  type: 'link';
  link: string;
}

export type SurveyQuestion =
  | SurveyRatingQuestion
  | SurveyOpenQuestion
  | SurveySingleChoiceQuestion
  | SurveyMultipleChoiceQuestion
  | SurveyLinkQuestion;

export interface Survey {
  id: string;
  name: string;
  type: SurveyType;
  base_language?: string;
  questions: SurveyQuestion[];
  appearance?: SurveyAppearance;
  conditions?: unknown;
  schedule?: SurveySchedule;
  start_date?: string;
  end_date?: string;
  current_iteration?: number | null;
  current_iteration_start_date?: string | null;
  internal_targeting_flag_key?: string;
  enable_partial_responses?: boolean;
}

export type SurveyResponseValue = string | number | string[] | null;

export interface PendingSurvey {
  survey: Survey;
  superProps: Record<string, unknown>;
}

/**
 * Bucket a rating value into negative/neutral/positive per PostHog's NPS conventions.
 * Used for `response_based` branching on rating questions.
 *
 * Buckets:
 *   scale 3   →  1 neg | 2 neu | 3 pos
 *   scale 5   →  1-2 neg | 3 neu | 4-5 pos
 *   scale 7   →  1-3 neg | 4-5 neu | 6-7 pos
 *   scale 10  →  0-6 neg | 7-8 neu | 9-10 pos  (NPS standard; isNpsQuestion is implied)
 */
/**
 * Compute the number of questions along any single path through the survey
 * starting from `startIndex`, but only if that number is the same for every
 * possible path. Returns `null` when branches diverge in length (e.g. one
 * choice leads to 2 more questions and another leads to 5).
 *
 * Used by the popover to decide whether a step indicator ("2 / 4") can be
 * shown honestly. When paths have different lengths, no fixed denominator
 * exists so the indicator is hidden entirely.
 *
 * The traversal mirrors `computeNextIndex` in the popover:
 *   - no branching → falls through to `idx + 1`
 *   - `end` branching → this question is terminal
 *   - `specific_question` → one deterministic jump
 *   - `response_based` → one branch per declared response value; the
 *     unhandled-response fallback (`idx + 1`) is intentionally NOT counted
 *     as a branch, since the mental model is "the paths the survey author
 *     defined". A cycle guard prevents infinite recursion on malformed
 *     surveys.
 */
export function computeUniformPathLength(
  questions: SurveyQuestion[],
  startIndex: number,
): number | null {
  const walk = (idx: number, visited: ReadonlySet<number>): number | null => {
    if (idx < 0 || idx >= questions.length) return 0;
    if (visited.has(idx)) return null;

    const nextVisited = new Set(visited);
    nextVisited.add(idx);

    const branching = questions[idx].branching;
    const downstream: (number | null)[] = [];

    if (!branching) {
      downstream.push(walk(idx + 1, nextVisited));
    } else if (branching.type === 'end') {
      downstream.push(0);
    } else if (branching.type === 'specific_question') {
      downstream.push(walk(branching.index, nextVisited));
    } else {
      const targets = Object.values(branching.responseValues);
      if (targets.length === 0) {
        downstream.push(walk(idx + 1, nextVisited));
      } else {
        for (const val of targets) {
          if (val === 'end') downstream.push(0);
          else downstream.push(walk(val, nextVisited));
        }
      }
    }

    if (downstream.some((v) => v === null)) return null;
    const first = downstream[0] as number;
    if (downstream.some((v) => v !== first)) return null;
    return 1 + first;
  };

  return walk(startIndex, new Set());
}

export function bucketRating(
  scale: SurveyRatingScale,
  isNps: boolean,
  value: number,
): SurveyResponseBucket {
  switch (scale) {
    case 3:
      if (value <= 1) return 'negative';
      if (value === 2) return 'neutral';
      return 'positive';
    case 5:
      if (value <= 2) return 'negative';
      if (value === 3) return 'neutral';
      return 'positive';
    case 7:
      if (value <= 3) return 'negative';
      if (value <= 5) return 'neutral';
      return 'positive';
    case 10:
      if (value <= 6) return 'negative';
      if (value <= 8) return 'neutral';
      return 'positive';
    default:
      return 'positive';
  }
}
